import { z } from "zod";

// Doctor's notes (clinical lead, #bug-reporting 05/11/2026): short notes the
// clinician hands to the patient for an employer, a school or an insurer.
// Built-in templates are the clinical lead's own wording (sick note,
// compression stockings, therapy coverage, orthotics); "custom" is a note the
// clinician dictates or types, with instructions on how to write it. Sent
// with a "Doctor's Note" note generation. Kept in step with
// @rosc/processing DOCTOR_NOTE_TEMPLATES.
export const DOCTOR_NOTE_KINDS = [
  "sick-note",
  "compression-stockings",
  "therapy-coverage",
  "orthotics",
  "custom",
] as const;
export const doctorNoteKindSchema = z.enum(DOCTOR_NOTE_KINDS);
export type DoctorNoteKind = z.output<typeof doctorNoteKindSchema>;

// Therapy coverage: the therapies the note asks coverage for. The clinical
// lead's wording names the first three.
export const DOCTOR_NOTE_THERAPIES = [
  "physiotherapy",
  "acupuncture",
  "massage-therapy",
  "chiropractic",
] as const;
export const doctorNoteTherapySchema = z.enum(DOCTOR_NOTE_THERAPIES);
export type DoctorNoteTherapy = z.output<typeof doctorNoteTherapySchema>;
export const DOCTOR_NOTE_DEFAULT_THERAPIES: readonly DoctorNoteTherapy[] = [
  "physiotherapy",
  "acupuncture",
  "massage-therapy",
];

export const DOCTOR_NOTE_CONTENT_MAX = 6000;
export const DOCTOR_NOTE_INSTRUCTIONS_MAX = 2000;

const chars = (max: number) => (value: string) =>
  Array.from(value).length <= max;
const block = (max: number) =>
  z
    .string()
    .transform((value) => value.normalize("NFC").replace(/\r\n?/g, "\n").trim())
    .pipe(z.string().refine(chars(max), `At most ${max} characters`));

export const doctorNoteOptionsSchema = z
  .strictObject({
    kind: doctorNoteKindSchema,
    // Custom: what the note should say, dictated or typed.
    content: block(DOCTOR_NOTE_CONTENT_MAX).optional(),
    // Custom: how to write it ("one paragraph", "address it to the school").
    instructions: block(DOCTOR_NOTE_INSTRUCTIONS_MAX).optional(),
    // Therapy coverage only. Missing: the clinical lead's three.
    therapies: z
      .array(doctorNoteTherapySchema)
      .min(1)
      .max(DOCTOR_NOTE_THERAPIES.length)
      .refine((list) => new Set(list).size === list.length, "Duplicate")
      .optional(),
  })
  .refine(
    (value) => value.kind !== "custom" || !!value.content,
    "A custom note needs its content",
  )
  .refine(
    (value) => value.kind === "custom" || value.content === undefined,
    "Only a custom note takes content",
  )
  .refine(
    (value) => value.kind === "therapy-coverage" || !value.therapies,
    "Therapies apply to therapy coverage only",
  );
export type DoctorNoteOptions = z.output<typeof doctorNoteOptionsSchema>;
